import { AlertCircle, X } from "lucide-react";

const tierDisplay = (tier) => {
  const priorityOptions = [
    { value: 2, title: "Tier 2 (Yellow flag)" },
    { value: 3, title: "Tier 3 (Red flag)" },
  ];
  return (
    priorityOptions.find((opt) => opt.value === tier)?.title || `Tier ${tier}`
  );
};

export default function RuleConflictError({
  existingRuleType,
  existingPercentage,
  existingCount,
  existingDays,
  existingTier,
  newRuleName,
  newPercentage,
  newCount,
  newDays,
  newTier,
  contactRole,
  onClose,
}) {
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-2 sm:p-4 lg:p-5">
      <div className="absolute inset-0 bg-black/30"></div>
      <div className="relative sm:ml-[130px] w-[90%] sm:w-[50%] xl:w-[40%] 2xl:w-[45%] max-h-[60vh] bg-white rounded shadow overflow-hidden z-[101] border border-[#DDDDDD]">
        {/* Header */}
        <div className="flex items-start justify-between px-3 sm:px-6 lg:px-5 py-3 sm:py-4 border-b border-b-[#DDDDDD]">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 mt-1">
              <div className="w-6 h-6 bg-red-50 rounded-full flex items-center justify-center">
                <AlertCircle className="w-4 h-4 text-red-600" />
              </div>
            </div>
            <div>
              <h4 className="text-base sm:text-lg font-semibold uppercase">
                Rule Conflict Detected
              </h4>
              <p className="text-sm sm:text-base font-medium ">
                Unable to create new rule due to strictness requirements
              </p>
            </div>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600 transition-colors duration-200 p-0.5"
            >
              <X className="w-6 h-6" />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="px-3 sm:px-4 lg:px-5 py-2 sm:py-4 space-y-4 sm:space-y-6 overflow-y-auto max-h-[calc(60vh-120px)]">
          {/* Main Message */}
          <div className="space-y-4">
            <div className="bg-slate-50 rounded p-2 sm:p-4">
              <h4 className="text-base sm:text-lg font-semibold uppercase mb-2">
                Existing Rule
              </h4>
              <p className="text-sm sm:text-base text-slate-700 leading-relaxed">
                A{" "}
                <span className="font-semibold text-slate-900">
                  {existingRuleType}
                </span>{" "}
                rule at{" "}
                <span className="font-semibold text-slate-900">
                  {tierDisplay(existingTier)}
                </span>{" "}
                is currently active at{" "}
                <span className="font-semibold text-slate-900">
                  {existingPercentage}%
                </span>{" "}
                for{" "}
                <span className="font-semibold text-slate-900">
                  {existingCount}
                </span>{" "}
                counts within{" "}
                <span className="font-semibold text-slate-900">
                  {existingDays}
                </span>{" "}
                days.
              </p>
            </div>

            <div className="bg-red-50 rounded p-2 sm:p-4 border border-red-100">
              <h4 className="text-base sm:text-lg font-semibold text-red-900 uppercase mb-2">
                Proposed Rule (Rejected)
              </h4>
              <p className="text-sm sm:text-base text-red-800 leading-relaxed">
                Your new <span className="font-semibold">{newRuleName}</span>{" "}
                rule at{" "}
                <span className="font-semibold">{tierDisplay(newTier)}</span>{" "}
                for <span className="font-semibold">{newPercentage}%</span> for{" "}
                <span className="font-semibold">{newCount}</span> counts within{" "}
                <span className="font-semibold">{newDays}</span> days cannot be
                created as it is less restrictive than the existing rule.
              </p>
            </div>
          </div>

          {/* Rule Comparison */}
          <div className="bg-white rounded border border-slate-200 overflow-hidden">
            <div className="px-2 sm:px-4 py-2 bg-slate-50 border-b border-slate-200">
              <h4 className="text-base sm:text-lg font-semibold text-slate-900 uppercase">
                Rule Comparison
              </h4>
            </div>
            <div className="p-2 sm:p-4">
              <div className="space-y-6">
                {/* Percentage Comparison */}
                <div className="space-y-4">
                  <h5 className="text-sm sm:text-base font-semibold text-slate-700 uppercase">
                    Percentage Threshold
                  </h5>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0 sm:space-x-6">
                    <div className="flex-1 bg-slate-50 rounded-lg p-4 sm:p-6">
                      <div className="text-center">
                        <div className="text-xl sm:text-2xl font-bold text-slate-900 mb-2">
                          {existingPercentage}%
                        </div>
                        <div className="text-xs sm:text-sm font-medium text-slate-600 uppercase tracking-wide">
                          Current Rule
                        </div>
                      </div>
                    </div>
                    <div className="flex-shrink-0 text-center">
                      <div className="text-slate-400 font-semibold text-base sm:text-lg">
                        VS
                      </div>
                    </div>
                    <div className="flex-1 bg-red-50 rounded-lg p-4 sm:p-6 border border-red-100">
                      <div className="text-center">
                        <div
                          className={`text-xl sm:text-2xl font-bold mb-2 ${
                            newPercentage >= existingPercentage
                              ? "text-red-600"
                              : "text-emerald-600"
                          }`}
                        >
                          {newPercentage}%
                        </div>
                        <div className="text-xs sm:text-sm font-medium text-slate-600 uppercase tracking-wide">
                          Proposed Rule
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Count Comparison */}
                <div className="space-y-4">
                  <h5 className="text-sm sm:text-base font-semibold text-slate-700 uppercase">
                    Count Threshold
                  </h5>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0 sm:space-x-6">
                    <div className="flex-1 bg-slate-50 rounded-lg p-4 sm:p-6">
                      <div className="text-center">
                        <div className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">
                          {existingCount}
                        </div>
                        <div className="text-xs sm:text-sm font-medium text-slate-600 uppercase tracking-wide">
                          Current Rule
                        </div>
                      </div>
                    </div>
                    <div className="flex-shrink-0 text-center">
                      <div className="text-slate-400 font-semibold text-base sm:text-lg">
                        VS
                      </div>
                    </div>
                    <div className="flex-1 bg-red-50 rounded-lg p-4 sm:p-6 border border-red-100">
                      <div className="text-center">
                        <div
                          className={`text-2xl sm:text-3xl font-bold mb-2 ${
                            newCount >= existingCount
                              ? "text-red-600"
                              : "text-emerald-600"
                          }`}
                        >
                          {newCount}
                        </div>
                        <div className="text-xs sm:text-sm font-medium text-slate-600 uppercase tracking-wide">
                          Proposed Rule
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Days Comparison */}
                <div className="space-y-4">
                  <h5 className="text-sm sm:text-base font-semibold text-slate-700 uppercase">
                    Time Period (Days)
                  </h5>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0 sm:space-x-6">
                    <div className="flex-1 bg-slate-50 rounded-lg p-4 sm:p-6">
                      <div className="text-center">
                        <div className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">
                          {existingDays}
                        </div>
                        <div className="text-xs sm:text-sm font-medium text-slate-600 uppercase tracking-wide">
                          Current Rule
                        </div>
                      </div>
                    </div>
                    <div className="flex-shrink-0 text-center">
                      <div className="text-slate-400 font-semibold text-base sm:text-lg">
                        VS
                      </div>
                    </div>
                    <div className="flex-1 bg-red-50 rounded-lg p-4 sm:p-6 border border-red-100">
                      <div className="text-center">
                        <div
                          className={`text-2xl sm:text-3xl font-bold mb-2 ${
                            newDays >= existingDays
                              ? "text-red-600"
                              : "text-emerald-600"
                          }`}
                        >
                          {newDays}
                        </div>
                        <div className="text-xs sm:text-sm font-medium text-slate-600 uppercase tracking-wide">
                          Proposed Rule
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Requirements */}
          <div className="bg-slate-50 rounded-xl p-4 sm:p-6 border border-slate-200">
            <h4 className="text-base sm:text-lg font-semibold text-slate-900 uppercase mb-2">
              Requirements for New Rule
            </h4>
            <p className="text-sm sm:text-base text-slate-700 mb-2 leading-relaxed">
              To create a more restrictive rule, at least one parameter must be
              more stringent than the existing rule:
            </p>
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <div className="w-1.5 h-1.5 bg-slate-400 rounded-full"></div>
                <span className="text-sm text-slate-700">
                  Lower percentage threshold
                </span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-1.5 h-1.5 bg-slate-400 rounded-full"></div>
                <span className="text-sm text-slate-700">
                  Lower count threshold
                </span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-1.5 h-1.5 bg-slate-400 rounded-full"></div>
                <span className="text-sm text-slate-700">
                  Shorter time period
                </span>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-slate-900 rounded-xl p-4 sm:p-6 text-center">
            <p className="text-sm sm:text-base text-slate-300 leading-relaxed">
              Need assistance with rule configuration?{" "}
              <span className="text-white font-semibold">
                Contact your {contactRole}
              </span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
